import React, { Component, Fragment } from 'react';
import ReactDOMServer from 'react-dom/server';
import { WithTranslation, withTranslation } from 'react-i18next';
import i18next from 'i18next';
import { connect } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { Extent } from 'ol/extent';
import { FitOptions } from 'ol/View';
import { IState } from 'services/root-reducer';
import { actions as omjsActions } from 'services/omjs/omjs-service';
import { routes } from 'routes';
import { EOperationStatus, IOperation } from 'types';
import { IGeometry } from 'types/omjs/project';
import { actions as mapActions, IMapStateOperationData } from 'services/map/map-service';
import { actions as containersActions } from 'services/containers/containers-service';
import { actions as objectWindowActions } from 'services/object-window/object-window-service';
import SmartOverlay from 'utils/smart-overlay';
import {
    getOperationData,
    getOperationSafe,
    getOmjsLayerConfig,
    getOmjsViewLayer
} from 'selectors';
import { PKK_API_PATH } from 'consts';
import { freeSpace, getMaxPolygonFromMultiPolygon } from 'utils';
import { deepClone } from 'utils/deep-equal';
import IconPowers from 'static/images/icon-powers.svg';
import IconGas from 'static/images/icon-gaz.svg';
import IconPowersDist from 'static/images/icon-powers-dist.svg';

import scaleLine from './controls/scale-line';
import sysLayerFabric, { ISysLayer } from './sys-layer-fabric';
import { UTFGrid } from './utf-grid';
import { Balloon } from './balloons';
import { PkkObjectBalloon } from './balloons/pkkObject';

import './style.scss';

const fixMoveStart = (map) => {
    const onPropertyChange = () => {
        map.dispatchEvent('movestart');
        const view = map.getView();
        view.un('propertychange', onPropertyChange);
        map.on('moveend', () => {
            view.on('propertychange', onPropertyChange);
        });
    };

    map.getView().on('propertychange', onPropertyChange);
};

const isExtentOfPoint = (extent: Extent) => extent[0] === extent[2] && extent[1] === extent[3];

enum EPointerInteraction {
    ENTER = 'pointerenter',
    MOVE = 'pointermove',
    LEAVE = 'pointerleave',
    CLICK = 'click'
}

export enum EBalloonTypes {
    PKKObject = 'pkk_object'
}

const iconPowersHTML = ReactDOMServer.renderToString(<IconPowers />);
const iconGasHTML = ReactDOMServer.renderToString(<IconGas />);
const iconPowersDistHTML = ReactDOMServer.renderToString(<IconPowersDist />);

const mobilePointerInteractions = [ EPointerInteraction.CLICK ];

interface IMapComponentProps extends WithTranslation {
    app: IState['app'];
    mapState: IState['operations']['MAP_STATE']['data'];
    centerFromUrl: string;
    zoomFromUrl: string;
    layersFromUrl: string;
    omjsOperation: IOperation;
    filtersOperation: IOperation;
}

interface IMapComponentState {
    mapInitialized: boolean;
}

class MapComponent extends Component<IMapComponentProps, IMapComponentState> {
    mapElement: HTMLDivElement;
    omjsView: omjs.View;
    tooltip;
    _clickTimer;
    _fitTimer;
    sysLayer: ISysLayer;
    mapIsMoving = false;
    _loaderTimer;
    utfGrid: UTFGrid;
    pkkTileLayer;
    pkkHighlightLayer;
    pkkLayersGroup = new omjs.ol.layer.Group({ visible: false });

    connectionLayers = {
        powers: new omjs.View.VectorLayer({ }),
        gas: new omjs.View.VectorLayer({ }),
        waterSupply: new omjs.View.VectorLayer({ }),
        sewerage: new omjs.View.VectorLayer({ })
    };
    indicatorsPopups: any[] = [];
    indicatorsLayersInjected: boolean = false;

    rTree: any;

    /** Слой, на котором отображаются результаты поиска по адресам яндекса */
    private yaAddressLayer = new omjs.ol.layer.Vector({
        style: new omjs.ol.style.Style({
            image: new omjs.ol.style.Icon({
                src: 'data:image/svg+xml;base64,PHN2ZyBpZD0ibWFya2VyIiB4bWxucz'
                     + '0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaG'
                     + 'VpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij4gIDxjaXJjbGUgZm'
                     + 'lsbD0iI2YwNWEzYyIgb3BhY2l0eT0iMC4yIiBjeD0iMTIiIGN5PSIxMi'
                     + 'Igcj0iMTIiLz4gIDxjaXJjbGUgZmlsbD0iI2YwNWEzYyIgc3Ryb2tlLX'
                     + 'dpZHRoPSIycHgiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZT'
                     + '0iI2ZmZiIgY3g9IjEyIiBjeT0iMTIiIHI9IjUiLz48L3N2Zz4=',
                anchor: [ 0.5, 0.5 ],
                imgSize: [ 24, 24 ] // imgSize необходим для корректной работы в IE
            })
        })
    });

    constructor(props) {
        super(props);
        this.state = {
            mapInitialized: false
        };

        this.rTree = (new omjs.ol.structs.RBush()).rbush_;

        this.processSmartOverlayMove = this.processSmartOverlayMove.bind(this);
    }

    componentDidMount() {
        this.initOMJS();
    }

    componentDidUpdate(prevProps: IMapComponentProps, prevState) {
        const {
            mapState,
            zoomFromUrl,
            centerFromUrl,
            omjsOperation
        } = this.props;
        const { mapState: prevMapState } = prevProps;
        const { mapInitialized } = this.state;

        if (omjsOperation.status === EOperationStatus.SUCCESS && !mapInitialized) {
            this.initMap();
        }

        if (!mapInitialized) {
            return;
        }

        const project = this.omjsView.getProject();
        const mapView = this.omjsView.getMap().getView();

        if (zoomFromUrl !== prevProps.zoomFromUrl || centerFromUrl !== prevProps.centerFromUrl) {
            const position = this.parsePositionFromUrl();
            if (position) {
                mapView.setCenter(position.center);
                mapView.setZoom(position.zoom);
            }
        }

        if (mapState.loader !== prevMapState.loader) {
            if (mapState.loader !== null) {
                this.showLoader(mapState.loader);
            } else {
                this.hideLoader();
            }
        }

        if (mapState.fitTarget !== prevMapState.fitTarget && mapState.fitTarget !== null) {
            if ('layer' in mapState.fitTarget && 'objectId' in mapState.fitTarget) {
                const { layer, objectId } = mapState.fitTarget;
                const container = project.getCachedContainers().find(
                    cont => cont.get(typeof layer === 'string' ? 'code' : 'id') === layer
                );
                const object = container.objects.getArray().find(obj => obj.getId() === objectId);
                const layerConfig = getOmjsLayerConfig(false, container.get('code'));
                const options: FitOptions = {};

                let sidebarBBox = this.getSidebarBoundingRect();
                options['padding'] = [ 0, 0, 0, sidebarBBox.width + sidebarBBox.left ];

                if (layerConfig['zoomLimit']) {
                    options['maxZoom'] = layerConfig['zoomLimit'];
                }

                if (isExtentOfPoint(object.feature.getGeometry().getExtent())) {
                    this.showLoader(object.feature.getGeometry().getCoordinates());
                } else if (object.feature.getGeometry().getType() === 'Polygon') {
                    const coords = object.feature.getGeometry().getInteriorPoint().getCoordinates();
                    this.showLoader(coords);
                } else if (object.feature.getGeometry().getType() === 'MultiPolygon') {
                    const largestPolygon = getMaxPolygonFromMultiPolygon(object.feature.getGeometry());
                    const coords = largestPolygon.getInteriorPoint().getCoordinates() as [number, number];
                    this.showLoader(coords);
                }
                this.fitToExtent(object.feature.getGeometry().getExtent(), options);
            } else if (Array.isArray(mapState.fitTarget)) {
                this.fitToExtent(mapState.fitTarget);
            }
        }

        if (mapState.fitExtent !== prevMapState.fitExtent && mapState.fitExtent !== null) {
            let sidebarBBox = this.getSidebarBoundingRect();
            let options: FitOptions = {};
            options['padding'] = [ 0, 0, 0, sidebarBBox.width + sidebarBBox.left ];

            this.fitToExtent(mapState.fitExtent, options);
        }

        if (mapState.activeRasterLayers.join(',') !== prevMapState.activeRasterLayers.join(',') && this.utfGrid) {
            this.utfGrid.setLayers(mapState.activeRasterLayers);
        }

        if (mapState.activeGpRasterLayers.length !== prevProps.mapState.activeGpRasterLayers.length && this.utfGrid) {
            this.utfGrid.setGpLayers(mapState.activeGpRasterLayers);
        }

        this.updateYaAddressMarker(prevMapState, mapState);
        if (mapState.connections.powers.enabled !== prevMapState.connections.powers.enabled) {
            this.indicatorsSetVisible('powers', mapState.connections.powers.enabled);
        }

        if (mapState.connections.gas.enabled !== prevMapState.connections.gas.enabled) {
            this.indicatorsSetVisible('gas', mapState.connections.gas.enabled);
        }

        if (mapState.connections.waterSupply.enabled !== prevMapState.connections.waterSupply.enabled) {
            this.indicatorsSetVisible('waterSupply', mapState.connections.waterSupply.enabled);
        }

        if (mapState.connections.sewerage.enabled !== prevMapState.connections.sewerage.enabled) {
            this.indicatorsSetVisible('sewerage', mapState.connections.sewerage.enabled);
        }

        if (mapState.connectionsRevision !== 0 && mapState.connectionsRevision !== prevMapState.connectionsRevision) {
            this.updatePowersLayer();
        }

        if (JSON.stringify(mapState.sysGeometry) !== JSON.stringify(prevMapState.sysGeometry)) {
            if (mapState.sysGeometry !== null) {
                this.showSysFeature(mapState.sysGeometry);
            } else {
                this.hideSysFeature();
            }
        }

        if (mapState.cadastreVisible !== prevMapState.cadastreVisible) {
            this.toggleCadastre(mapState.cadastreVisible);
        }

        if (
            JSON.stringify(mapState.cadastreHighlightGeometry)
                !== JSON.stringify(prevMapState.cadastreHighlightGeometry)
        ) {
            this.highlightCadastreGeometry(mapState.cadastreHighlightGeometry);
        }
    }

    getSidebarBoundingRect() {
        let sidebarEl = document.querySelector('.oms-sidebar-wrapper');

        if (sidebarEl === null) {
            return {
                y: 0,
                x: 0,
                width: 0,
                height: 0,
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            };
        }

        let map = this.omjsView.getMap();
        let mapEl = map.getTargetElement();

        let {
            bottom,
            y,
            x,
            width,
            top,
            right,
            left,
            height
        }: DOMRect = sidebarEl.getBoundingClientRect();
        let mapBBox = mapEl.getBoundingClientRect();

        return {
            y,
            x,
            width,
            height,
            top: top - mapBBox.top,
            right: right - mapBBox.right,
            bottom: bottom - mapBBox.bottom,
            left: left - mapBBox.left
        };
    }

    getLayerSettings(key: number | string) {
        const { config } = this.props.app;
        const layersConfig = config.publication.layers_config;
        const attr = typeof key === 'string' ? 'code' : 'id';
        const container = this.omjsView.getProject()['getCachedPubContainers']().find(cont => cont.get(attr) === key);
        return layersConfig.find(item => item.containerId === container.get('id'));
    }

    getSingleFeature(feature) {
        if (!feature.get('features')) {
            return feature;
        } else if (feature.get('features').length > 0) {
            return feature.get('features')[0];
        } else {
            return false;
        }
    }

    getLegendHTML(layer, obj) {
        const element = document.createElement('div');
        element.className = 'chart-legend';
        element.innerHTML = `<div class="chart-legend__name">${obj.get('name')}</div>`;
        for (let item of layer._legend.classificators) {
            const column = layer.structure.find(c => c.name.trim() === item.value.trim());

            if (!column) {
                return '';
            }

            const columnVal = obj.get(column.code);
            if (columnVal !== 0) {
                const itemEl = document.createElement('div');
                itemEl.className = 'chart-legend-item';
                itemEl.innerHTML = `<span>${item.value} (${columnVal})</span>`;
                const clr = document.createElement('div');
                clr.className = 'chart-legend-item__clr';
                clr.style.backgroundColor = item.fill;
                itemEl.appendChild(clr);
                element.appendChild(itemEl);
            }
        }
        const wrapper = document.createElement('div');
        wrapper.appendChild(element);
        return wrapper.innerHTML;
    }

    setVectorLayerInteraction(layer) {
        const { mapState, app } = this.props;
        const { config } = app;
        const layersConfig = config.publication.layers_config;

        layer.on(EPointerInteraction.ENTER, (e) => {
            const feature = this.getSingleFeature(e.feature);

            if (this.beforeLayerInteraction(EPointerInteraction.ENTER)) {
                const pLayer = layer.getProjectLayers().at(0);
                const layerSettings = layersConfig.find(item => item.containerId === pLayer.get('id'));
                let legend = false;
                if (pLayer.get('style_mode') === 3) {
                    legend = pLayer.get('visualSettings').showLegend;
                }
                if (layerSettings?.balloonEnabled || layerSettings?.objectWindowEnabled || legend) {
                    this.omjsView.getMap().getTargetElement().style.cursor = 'pointer';
                    e.feature.set('highlighted', true);
                }

                if (pLayer.get('vectorSettings').showPopup) {
                    const code = pLayer.get('vectorSettings').popupColumnCode.replace(/_ru$/, '');
                    const text = feature.get('obj').fget(code);
                    if (this.tooltip) {
                        this.tooltip.getElement().innerHTML = text;
                        this.tooltip.setPosition(e.olEvent.coordinate);
                    } else {
                        this.showTooltip(e.olEvent.coordinate, text);
                    }
                }

                let obj = e.feature.get('obj');
                if (!obj) {
                    const features = e.feature.get('features');
                    if (features.length === 1) {
                        obj = features[0].get('obj');
                    }
                }
                if (legend && obj) {
                    if (pLayer._legend) {
                        const legendHTML = this.getLegendHTML(pLayer, obj);
                        if (this.tooltip) {
                            this.tooltip.getElement().innerHTML = legendHTML;
                            this.tooltip.setPosition(e.olEvent.coordinate);
                        } else {
                            this.showTooltip(e.olEvent.coordinate, legendHTML);
                        }
                    }
                }
            }
        });

        layer.on(EPointerInteraction.MOVE, (e) => {
            if (this.beforeLayerInteraction(EPointerInteraction.MOVE)) {
                const pLayer = layer.getProjectLayers().at(0);
                const layerSettings = layersConfig.find(item => item.containerId === pLayer.get('id'));

                let legend = false;
                if (pLayer.get('style_mode') === 3) {
                    legend = pLayer.get('visualSettings').showLegend;
                }

                if (layerSettings?.balloonEnabled || layerSettings?.objectWindowEnabled || legend) {
                    this.omjsView.getMap().getTargetElement().style.cursor = 'pointer';
                }

                if (this.tooltip) {
                    this.tooltip.setPosition(e.olEvent.coordinate);
                }
            }
        });

        layer.on(EPointerInteraction.LEAVE, (e) => {
            if (this.beforeLayerInteraction(EPointerInteraction.LEAVE)) {
                this.omjsView.getMap().getTargetElement().style.cursor = '';
                this.hideTooltip();

                if (e.feature.get('highlighted')) {
                    e.feature.set('highlighted', false);
                }
            }
        });

        layer.on(EPointerInteraction.CLICK, (e) => {
            const feature = this.getSingleFeature(e.feature);
            if (this.beforeLayerInteraction(EPointerInteraction.CLICK)) {
                if (mapState.balloonType) {
                    mapActions.hideBalloon();
                }
                const pLayer = layer.getProjectLayers().at(0);

                if (this._clickTimer !== undefined) {
                    this._clickTimer = clearTimeout(this._clickTimer);
                    return;
                }

                this._clickTimer = setTimeout(() => {
                    this._clickTimer = undefined;
                    const obj = feature.get('obj');
                    const layerSettings = this.getLayerSettings(obj.options.layerCode);

                    let legend = false;
                    if (pLayer.get('style_mode') === 3) {
                        legend = pLayer.get('visualSettings').showLegend;
                    }

                    let eObj = e.feature.get('obj');
                    if (!eObj) {
                        const features = e.feature.get('features');
                        if (features.length === 1) {
                            eObj = features[0].get('obj');
                        }
                    }

                    if (legend && eObj) {
                        if (pLayer._legend) {
                            const legendHTML = this.getLegendHTML(pLayer, eObj);
                            if (this.tooltip) {
                                this.tooltip.getElement().innerHTML = legendHTML;
                                this.tooltip.setPosition(e.olEvent.coordinate);
                            } else {
                                this.showTooltip(e.olEvent.coordinate, legendHTML);
                            }
                        }
                    }

                    if (layerSettings?.balloonEnabled || layerSettings?.objectWindowEnabled) {
                        mapActions.showLoader(e.feature.get('geometry').getCoordinates());
                        routes.redirect(routes.object, {
                            layerCode: obj.options.layerCode,
                            objectId: obj.getId()
                        });
                    }
                }, 300);
            }
        });
    }

    setMapPositionToUrl() {
        const mapView = this.omjsView.getMap().getView();
        let center = omjs.ol.proj.toLonLat(mapView.getCenter());
        center = [ center[0].toFixed(6), center[1].toFixed(6) ].join(',');
        const zoom = Math.round(omjs.Utils.getZoomFromResolution(mapView.getResolution()));
        routes.setMapParams({ center, zoom });

        if (zoom < 12) {
            this.omjsView.getContainer().addClass('hide-connections-popups');
        } else {
            this.omjsView.getContainer().removeClass('hide-connections-popups');
        }
        this.calcChartsOverlap();
    }

    setHighlightGeometry() {
        const features = this.sysLayer?.layer.getSource().getFeatures();
        if ((features.length > 0) && (features[0].getGeometry().getType() !== 'Point')) {
            objectWindowActions.setHighlightGeometry();
        }
    }

    getConnectionsStyles(type) {
        const colors = {
            powers: '#EBBE36',
            gas: '#3399CC',
            waterSupply: '#0057FF',
            sewerage: '#924D1B'
        };
        return [
            new omjs.ol.style.Style({
                stroke: new omjs.ol.style.Stroke({
                    color: '#ffffff',
                    width: 4
                })
            }),
            new omjs.ol.style.Style({
                stroke: new omjs.ol.style.Stroke({
                    color: colors[type] || '#3399CC',
                    width: 2
                })
            })
        ];
    }

    beforeVectorAdd = (container, loadOptions) => {
        const newOptions = { ...loadOptions };
        const { filtersOperation: { data: filters } } = this.props;

        if (!this.indicatorsLayersInjected) {
            this.indicatorsLayersInjected = true;
            this.initIndicatorsLayers();
        }

        if (container.get('style_mode') === 3) {
            const visSettings = container.get('visualSettings');
            if (visSettings.type === 4 && visSettings.classesSettings.sizeType === 3) {
                newOptions.fields = [ 'razm', 'name' ];
            }
            if (container.get('visualSettings').showLegend) {
                omjsActions.loadLegend(container.get('code'));
            }
        }

        if (filters === null) {
            return newOptions;
        }

        const fieldsForLoad = new Set(newOptions.fields);
        for (let filtersGroupKey of Object.keys(filters)) {
            filters[filtersGroupKey].forEach((filter) => {
                for (let _contKey of Object.keys(filter.settingsObjects)) {
                    if (_contKey === container.get('code')) {
                        fieldsForLoad.add(filter.settingsObjects[_contKey]);
                    }
                }
                // если это объектный список, и по текущему контейнеру возможен список
                if (filter.type === 'list_objects' && container.get('code') in filter.settingsFilter) {
                    fieldsForLoad.add(filter.settingsFilter[container.get('code')]);
                }
            });
        }

        newOptions.fields = [ ...fieldsForLoad ];

        if ([ 'sites_1', 'sites_2', 'sites_3', 'sites_4' ].includes(container.get('code'))) {
            newOptions.fields = [ ...newOptions.fields, 'name_owner_maps', 'contact_person_maps' ];
        }

        if (container.get('code') === 'ia34') {
            newOptions.fields = [ ...newOptions.fields, 'power' ];
        }
        if (container.get('code') === 'ii_obj_gazosnabzh_new') {
            newOptions.fields = [ ...newOptions.fields, 'throughput_gas' ];
        }
        if (container.get('json')?.cms_geom_style) {
            newOptions.fields = [ ...newOptions.fields, 'geom_style' ];
        }

        return newOptions;
    };

    beforeLayerInteraction(interaction: EPointerInteraction): boolean {
        if (this['measureOn']) {
            return false;
        }

        if (isMobile && mobilePointerInteractions.indexOf(interaction) === -1) {
            return false;
        }

        if (interaction === EPointerInteraction.ENTER) {
            if (this.utfGrid) {
                this.utfGrid.layer.disable();
            }
        }

        if (interaction === EPointerInteraction.LEAVE) {
            if (this.utfGrid) {
                this.utfGrid.layer.enable();
            }
        }
        return true;
    }

    updateYaAddressMarker(
        prev_map_data: IMapStateOperationData,
        current_map_data: IMapStateOperationData
    ) {
        if (current_map_data.yaAddressMarker !== prev_map_data.yaAddressMarker) {
            if (current_map_data.yaAddressMarker !== null) {
                // создать маркер или передвинуть его на новую позицию
                let geom = new omjs.ol.geom.Point(current_map_data.yaAddressMarker);
                let feature = new omjs.ol.Feature({ geometry: geom });
                this.yaAddressLayer.setSource();
                this.yaAddressLayer.setSource(
                    new omjs.ol.source.Vector({ features: [ feature ] })
                );
            } else {
                // очистить слой с маркером
                this.yaAddressLayer.setSource();
            }
        }
    }

    makeVectorLayersInteractive() {
        this.omjsView.getLayers().each((layer) => {
            if (!layer.getProjectLayers || !layer.getProjectLayers() || layer.getProjectLayers().getLength() === 0) {
                return;
            }
            if ([ 1, 3 ].includes(layer.getProjectLayers().at(0).get('render_type'))) {
                this.setVectorLayerInteraction(layer);
            }
        });
    }

    parsePositionFromUrl(): { zoom: number, center: [ number, number ]} | false {
        const { zoomFromUrl, centerFromUrl } = this.props;
        if (zoomFromUrl && centerFromUrl) {
            let zoom = parseInt(zoomFromUrl);
            let center = centerFromUrl.split(',') as [any, any];
            if (center.length === 2 && zoom) {
                center = [ parseFloat(center[0]), parseFloat(center[1]) ];
                center = omjs.ol.proj.fromLonLat(center);
                return { center, zoom };
            }
        }

        return false;
    }

    initOMJS() {
        const { layersFromUrl } = this.props;
        const { config, authToken } = this.props.app;
        let layers = [];
        let initViewConfig = deepClone(config);

        if (layersFromUrl) {
            layers = layersFromUrl.split(',').map(layerId => parseInt(layerId));
        }

        const position = this.parsePositionFromUrl();
        if (position) {
            initViewConfig.publication.map_position.zoom = position.zoom;
            initViewConfig.publication.map_position.center = position.center;
        }

        let viewInitOptions = {
            lng: i18next.language
        };

        if (authToken) {
            viewInitOptions['token'] = authToken;
        }

        omjs.initView(this.mapElement, initViewConfig, viewInitOptions).then((view) => {
            this.omjsView = view;
            omjsActions.setInstances({ view, layers });
        });
    }

    initIndicatorsLayers() {
        for (let key in this.connectionLayers) {
            if (key in this.connectionLayers) {
                const source = new omjs.ol.source.Vector();
                this.connectionLayers[key].setSource(source);
                this.connectionLayers[key].getMapLayer().set('zIndex', 1);
                this.connectionLayers[key].getMapLayer().setMaxResolution(39);
                this.connectionLayers[key].getMapLayer().setStyle(this.getConnectionsStyles(key));
                this.omjsView.addLayer(this.connectionLayers[key]);
            }
        }
        this.setMapPositionToUrl();
    }

    createIndicatorsAndPopups(connections, type) {
        const { t } = this.props;
        const icons = {
            powers: iconPowersHTML,
            gas: iconGasHTML
        };
        const features = [];
        connections.forEach((obj) => {
            const feature = new omjs.ol.Feature({
                geometry: new omjs.ol.geom.LineString([ obj.coordinateFrom, obj.coordinateTo ])
            });
            features.push(feature);

            const overlayEl = document.createElement('DIV');
            overlayEl.className = `connection-popup ${type}-popup`;
            const units = (type === 'powers') ? t('МВт') : t('тыс.м3/час');
            let text = `
                <div>
                    <span>${iconPowersDistHTML}</span>
                    <span>${(obj.distance / 1000).toFixed(1)} ${t('км')}</span>
                </div>
            `;

            if (typeof obj.power === 'number' && !Number.isNaN(obj.power)) {
                text += `<div>
                    <span>${icons[type] || iconPowersHTML}</span>
                    <span>${obj.power.toFixed(2)} ${units}</span>
                </div>`;
            }
            overlayEl.innerHTML = text;
            const overlay = new omjs.ol.Overlay({
                element: overlayEl,
                autoPan: true,
                position: obj.coordinateTo,
                positioning: 'bottom-center',
                autoPanAnimation: {
                    duration: 250
                }
            });
            this.indicatorsPopups.push(overlay);
        });
        return features;
    }

    updatePowersLayer() {
        const { connections } = this.props.mapState;

        for (let key in connections) {
            if (!(key in this.connectionLayers)) return;
        }

        this.indicatorsPopups.forEach((p) => {
            this.omjsView.getMap().removeOverlay(p);
        });
        this.indicatorsPopups.length = 0;

        for (let key in this.connectionLayers) {
            if (key in connections) {
                this.connectionLayers[key].getSource().clear();
                const features = this.createIndicatorsAndPopups(connections[key].objects, key);
                this.connectionLayers[key].getSource().addFeatures(features);
                this.indicatorsSetVisible(key, connections[key].enabled);
            }
        }

        this.indicatorsPopups.sort((a, b) => a.get('position')[1] - b.get('position')[1]);
        this.indicatorsPopups.forEach((popup) => {
            this.omjsView.getMap().addOverlay(popup);
        });
    }

    indicatorsSetVisible(layerType, visible: boolean) {
        const layer = this.connectionLayers[layerType];
        const popupClass = `disable-${layerType}-popups`;
        if (layer) {
            if (visible) {
                layer.show();
                this.omjsView.getContainer().removeClass(popupClass);
            } else {
                layer.hide();
                this.omjsView.getContainer().addClass(popupClass);
            }
        }
    }

    initMap() {
        this.omjsView.showOMSPublication(this.beforeVectorAdd).done(() => {
            this.omjsView.getMap().addControl(scaleLine);
            this.makeVectorLayersInteractive();

            this.omjsView.getLayers().getArray().forEach((l) => {
                const projectLayers = l?.getProjectLayers();
                if (!projectLayers) return;
                const find = projectLayers.getArray()
                    .find(
                        pl => [ 'ia34', 'ii_obj_gazosnabzh_new', 'water_supply', 'sewerage' ].includes(pl.get('code'))
                    );
                if (find) l.getMapLayer().set('zIndex', 2);
            });
            // fix for ol version < 4.2.0
            // fixMoveStart(this.omjsView.getMap());

            this.omjsView.getMap().on('movestart', () => {
                this.mapIsMoving = true;
                this.omjsView['mapEventsOff']();
            });
            this.omjsView.getMap().on('moveend', () => {
                this.mapIsMoving = false;
                this.omjsView['mapEventsOn']();
                this.setMapPositionToUrl();
                this.setHighlightGeometry();
                const zoom = this.omjsView.getMap().getView().getZoom();
                const center = this.omjsView.getMap().getView().getCenter();
                if (i18next.language === 'ru') {
                    omjsActions.watchForGenPlanes({ zoom, center });
                }
            });
            this.sysLayer = sysLayerFabric(this.omjsView);
            this.initCadastreLayers();
            this.setState({ mapInitialized: true });
            containersActions.initFilters();

            this.omjsView.addLayer(this.yaAddressLayer, {
                layerType: 'vector'
            }).done(() => {
                this.yaAddressLayer.setZIndex(999999999);
            });

            const zoom = this.omjsView.getMap().getView().getZoom();
            const center = this.omjsView.getMap().getView().getCenter();
            if (i18next.language === 'ru') {
                // omjsActions.prepareGenPlanes({ zoom, center });
                omjsActions.watchForGenPlanes(null);
            }
            this.initUtfGrid();

            this.updatePowersLayer();

            const chartsLayer = getOmjsViewLayer(false, 'proizvodstvo');

            chartsLayer.getMapLayer().on('change', () => {
                const featuresLength = chartsLayer.getSource().getFeatures().length;

                if (
                    featuresLength
                    && (!chartsLayer['fullSource'] || chartsLayer['fullSource'].getFeatures().length === 0)
                ) {
                    this.calcChartsOverlap();
                }
            });

            this.calcChartsOverlap();
            if (i18next.language !== 'ru') {
                containersActions.setContainerSelected({ id_or_code: 'folder71', selected: false });
                containersActions.setContainerSelected({ id_or_code: 'folder88', selected: false });
                containersActions.setContainerSelected({ id_or_code: 'folder12', selected: false });
                containersActions.setContainerSelected({ id_or_code: 'productive_agricultural_land', selected: false });
            }
        });
    }

    calcChartsOverlap() {
        const chartsLayer = getOmjsViewLayer(false, 'proizvodstvo');
        const pLayer = chartsLayer?.getProjectLayers().at(0);
        if (!pLayer || chartsLayer['_olClusterSource']) {
            return;
        }
        const objects = [ ...pLayer.objects.getArray() ];
        const { classesSettings } = pLayer.get('visualSettings');
        let minV = 2;
        let maxV = 5;
        this.rTree.clear();
        const resolution = this.omjsView.getMap().getView().getResolution();

        objects.sort((a, b) => b.get('razm') - a.get('razm'));

        const filtered = [];
        objects.forEach((obj) => {
            let sizeDelta = classesSettings.sizeTo - classesSettings.sizeFrom;
            let value = obj.get('razm');
            let chartSize = parseInt(
                classesSettings.sizeFrom + sizeDelta * ((value - minV) / (maxV - minV))
            );
            const halfSize = Math.floor(chartSize / 2);

            let coords = obj.getGeometry().coordinates;

            let item = {
                minX: coords[0] - (resolution * halfSize),
                minY: coords[1] - (resolution * halfSize),
                maxX: coords[0] + (resolution * halfSize),
                maxY: coords[1] + (resolution * halfSize),
                objId: obj.getId()
            };

            if (!this.rTree.collides(item)) {
                this.rTree.insert(item);
                filtered.push(obj.getId());
            }
        });

        if (!chartsLayer['fullSource'] || chartsLayer['fullSource'].getFeatures().length === 0) {
            chartsLayer['fullSource'] = new omjs.ol.source.Vector({});
            chartsLayer['fullSource'].addFeatures(chartsLayer.getSource().getFeatures());
        }

        const filteredFeatures = chartsLayer['fullSource'].getFeatures().filter(
            f => filtered.includes(f.get('obj').getId())
        );
        chartsLayer.getSource().clear();
        chartsLayer.getSource().addFeatures(filteredFeatures);
    }

    initUtfGrid() {
        const { mapState } = this.props;
        this.utfGrid = new UTFGrid(
            this.omjsView,
            mapState.activeRasterLayers,
            (coordinate, text) => this.showTooltip(coordinate, text),
            () => this.hideTooltip(),
            (coordinate) => {
                if (this.tooltip) {
                    this.tooltip.setPosition(coordinate);
                }
            }
        );
    }

    processSmartOverlayMove() {
        let map = this.omjsView.getMap();
        let { tooltip } = this;
        let tooltipCoordinate = tooltip.getPosition();
        let mapExtent = map.getView().calculateExtent(map.getSize());

        if (!omjs.ol.extent.containsXY(mapExtent, tooltipCoordinate[0], tooltipCoordinate[1])) {
            this.hideTooltip();
        }
    }

    hideTooltip() {
        if (this.tooltip) {
            this.tooltip.setPosition(undefined);
            this.omjsView.getMap().removeOverlay(this.tooltip);
            this.tooltip = false;
            this.omjsView.getMap().un('moveend', this.processSmartOverlayMove);
        }
    }

    showTooltip(coordinate, text: string | Node) {
        if (text) {
            this.hideTooltip();
            const element = document.createElement('div');
            element.className = 'omjs-popup-tooltip';
            if (typeof text === 'string') {
                element.innerHTML = text;
            } else {
                element.appendChild(text);
            }
            this.tooltip = new SmartOverlay({
                'positioning': 'top-center',
                'offset': [ 0, 16 ],
                position: coordinate,
                'element': element
            });

            this.omjsView.getMap().addOverlay(this.tooltip);
            this.omjsView.getMap().on('moveend', this.processSmartOverlayMove);
            this.tooltip.updatePixelPosition();
        }
    }

    showLoader(coordinates: [ number, number ]) {
        clearTimeout(this._loaderTimer);
        this.sysLayer.show();
        this.sysLayer.showLoader(coordinates);
        this._loaderTimer = setTimeout(() => {
            this.sysLayer.hideLoader();
        }, 5000);
    }

    hideLoader() {
        this.sysLayer.hideLoader();
        this.sysLayer.hide();
    }

    showSysFeature(geom: IGeometry) {
        this.sysLayer.clearPoly();
        this.sysLayer.show();
        this.sysLayer.addGeometry(geom);
    }

    hideSysFeature() {
        this.sysLayer.removeGeometry();
        this.sysLayer.hide();
    }

    fitToExtent(extent: Extent, options = {}) {
        const { config } = this.props.app;
        const mapPositionConfig = config.publication.map_position;
        const map = this.omjsView.getMap();
        const mapView = map.getView();

        const fitOptions = {
            maxZoom: 16, // mapPositionConfig.default_layer_zoom,
            ...options,
            constrainResolution: true,
            duration: 700,
            size: map.getSize()
        };

        mapView.fit(extent, fitOptions);
        clearTimeout(this._fitTimer);
        this._fitTimer = setTimeout(() => {
            const targetZoom = omjs.Utils.getZoomFromResolution(mapView.getResolution());
            mapView.setZoom(Math.round(targetZoom));
            mapActions.fitToTarget(null);
            omjsActions.watchForGenPlanes({ zoom: Math.round(targetZoom), center: mapView.getCenter() });
        }, 800);
    }

    initCadastreLayers() {
        const options: any = {};

        const pixelRatio = Math.round(window.devicePixelRatio);
        if (pixelRatio > 1) {
            options.retina = pixelRatio;
        }

        let params = '';
        for (const param in options) {
            if (Object.prototype.hasOwnProperty.call(options, param)) {
                params += `${param === Object.keys(options)[0] ? '?' : '&'}${param}=${options[param]}`;
            }
        }

        const { protocol, hostname } = this.omjsView.getProject().apiUrlLocation;
        this.pkkTileLayer = new omjs.ol.layer.Tile({
            source: new omjs.ol.source.XYZ({
                url: `${protocol}//${hostname}/${PKK_API_PATH}/tms/{z}/{x}/{y}.png${params}`,
                tilePixelRatio: pixelRatio,
                crossOrigin: 'anonymous'
            })
        });

        this.pkkHighlightLayer = new omjs.ol.layer.Vector({
            source: new omjs.ol.source.Vector(),
            style: new omjs.ol.style.Style({
                fill: new omjs.ol.style.Fill({
                    color: 'rgba(255, 255, 0, 0.4)'
                }),
                stroke: new omjs.ol.style.Stroke({
                    color: 'rgba(255, 255, 0, 0.4)',
                    width: 6
                }),
                image: new omjs.ol.style.Circle({
                    fill: new omjs.ol.style.Fill({
                        color: 'rgba(255, 255, 0, 0.4)'
                    }),
                    radius: 12,
                    stroke: new omjs.ol.style.Stroke({
                        color: 'rgba(255, 255, 0, 0.4)',
                        width: 6
                    })
                })
            })
        });

        this.pkkLayersGroup.getLayers().getArray().push(this.pkkTileLayer, this.pkkHighlightLayer);
        this.pkkLayersGroup.set('code', 'PKK');

        this.omjsView.getMap().getLayers().insertAt(1, this.pkkLayersGroup);

        this.pkkLayersGroup.setVisible(this.props.mapState.cadastreVisible);
    }

    toggleCadastre(selected: boolean) {
        this.pkkLayersGroup.setVisible(selected);
    }

    highlightCadastreGeometry(geom: IGeometry | null) {
        const source = this.pkkHighlightLayer?.getSource();
        if (source) {
            source.clear();
            if (geom) {
                source.addFeatures(new omjs.ol.format.GeoJSON().readFeatures(geom));
            }
            this.omjsView.getMap().renderSync();
        }
    }

    renderBalloon() {
        const { mapState } = this.props;
        switch (mapState.balloonType) {
            case EBalloonTypes.PKKObject: return <PkkObjectBalloon />;
            default: return null;
        }
    }

    render() {
        const { mapState } = this.props;
        return (
            <Fragment>
                <div
                    id='map_container'
                    ref={(el) => { this.mapElement = el as HTMLDivElement; }}
                />
                <Balloon
                    view={this.omjsView}
                    coords={mapState.balloonCoords}
                    freeSpaceElement={freeSpace.element}
                    draggable
                >
                    {this.renderBalloon()}
                </Balloon>
            </Fragment>
        );
    }
}

const mapStateToProps = (state: IState) => ({
    app: state.app,
    mapState: getOperationData(state, 'MAP_STATE'),
    omjsOperation: getOperationSafe(state, 'OMJS_READY'),
    filtersOperation: getOperationSafe(state, 'FILTERS')
});

export const Map = withTranslation()(connect(mapStateToProps)(MapComponent));
